import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Bubble-level style visualization for tilt sensor data.
 * Shows roll and pitch values with color-coded indicator.
 */
export default function TiltSensorGauge({ tiltSensor, onCalibrate, isCalibrating = false }) {
  const { t } = useTranslation();

  if (!tiltSensor) {
    return null;
  }

  const roll = tiltSensor.calibrated_roll;
  const pitch = tiltSensor.calibrated_pitch;
  const hasData = roll != null && pitch != null;

  const maxAngle = 15; // Max angle for visualization
  const size = 120;
  const center = size / 2;
  const radius = (size - 20) / 2;

  // Calculate dot position (clamp to maxAngle)
  const clampedRoll = Math.max(-maxAngle, Math.min(maxAngle, roll || 0));
  const clampedPitch = Math.max(-maxAngle, Math.min(maxAngle, pitch || 0));
  const dotX = center + (clampedRoll / maxAngle) * radius;
  const dotY = center - (clampedPitch / maxAngle) * radius;

  // Calculate color based on total tilt
  const totalTilt = Math.sqrt((roll || 0) ** 2 + (pitch || 0) ** 2);
  const dotColor = totalTilt < 3 ? "#4CAF50" : totalTilt < 8 ? "#FFC107" : "#F44336";

  return (
    <div className="tilt-sensor-display p-3 text-center">
      <div className="mb-2">
        <strong>{t("tilt_sensor", "Neigungssensor")}</strong>
      </div>

      <div className="d-flex flex-column align-items-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Background circles */}
          <circle cx={center} cy={center} r={radius} fill="#f5f5f5" stroke="#ddd" strokeWidth="1" />
          <circle
            cx={center}
            cy={center}
            r={radius * 0.66}
            fill="none"
            stroke="#eee"
            strokeWidth="1"
          />
          <circle
            cx={center}
            cy={center}
            r={radius * 0.33}
            fill="none"
            stroke="#eee"
            strokeWidth="1"
          />

          {/* Cross lines */}
          <line
            x1={center - radius}
            y1={center}
            x2={center + radius}
            y2={center}
            stroke="#ddd"
            strokeWidth="1"
          />
          <line
            x1={center}
            y1={center - radius}
            x2={center}
            y2={center + radius}
            stroke="#ddd"
            strokeWidth="1"
          />

          {/* Center point */}
          <circle cx={center} cy={center} r="3" fill="#999" />

          {/* Tilt indicator dot */}
          {hasData && (
            <circle cx={dotX} cy={dotY} r="8" fill={dotColor} stroke="#fff" strokeWidth="2">
              <title>
                Roll: {Number(roll).toFixed(1)}° | Pitch: {Number(pitch).toFixed(1)}°
              </title>
            </circle>
          )}

          {/* Labels */}
          <text x={center} y="8" textAnchor="middle" fontSize="9" fill="#666">
            +P
          </text>
          <text x={center} y={size - 2} textAnchor="middle" fontSize="9" fill="#666">
            -P
          </text>
          <text x={size - 4} y={center + 3} textAnchor="end" fontSize="9" fill="#666">
            +R
          </text>
          <text x="4" y={center + 3} textAnchor="start" fontSize="9" fill="#666">
            -R
          </text>
        </svg>

        {/* Value display */}
        <div className="d-flex justify-content-center gap-4 mt-2">
          <div>
            <div className="text-muted small">Roll</div>
            <div className="fs-5 fw-semibold">{hasData ? `${Number(roll).toFixed(1)}°` : "-"}</div>
          </div>
          <div>
            <div className="text-muted small">Pitch</div>
            <div className="fs-5 fw-semibold">{hasData ? `${Number(pitch).toFixed(1)}°` : "-"}</div>
          </div>
        </div>

        {/* Calibrate button */}
        {onCalibrate && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mt-3"
            onClick={onCalibrate}
            disabled={isCalibrating}
          >
            {isCalibrating ? t("calibrating", "Kalibriere...") : t("tilt_calibrate", "Nullen")}
          </button>
        )}
      </div>
    </div>
  );
}
